'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavbarToggler } from 'reactstrap';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { toast } from 'react-toastify';
import {
	OpenJobsIcon,
	DashboardIcon,
	ClosedJobsIcon,
	HelpIcon,
	QuotesIcon,
	InvoicesIcon,
	DocumentsIcon,
	LogoIcon,
	SettingsIcon,
	LogGreyIcon,
	OpenMakeReadiesIcon,
	ClosedMakeReadiesIcon,
	FlatPriceCatalogSidebarIcon,
} from 'src/assets/images/svg-icons';
import { AuthContext } from 'src/context/auth-context/auth-context';
import { getQuotesCount } from 'src/services/quotes';
import { handleErrorMessages, openInANewTab } from 'src/helpers/utils';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';

const Sidebar = () => {
	const pathname = usePathname();
	const hideMenu = () => {
		document.getElementById('sidebarNav')?.classList.remove('open');
	};
	const [userDetails] = useContext(AuthContext);
	const [quoteCount, setQuoteCount] = useState<string | number>('');
	const dispatch = useAppDispatch();

	const timeOutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	const fetchQuotesCount = useCallback(() => {
		if (!document.hidden) {
			getQuotesCount({
				quote_status: 'pending_approval',
				response_data: 'count',
			})
				.then((res) => {
					if (res && typeof res === 'object' && 'data' in res) {
						const data = res.data as Record<string, unknown>;
						setQuoteCount((data?.quote_count as string | number) || '');
					}
				})
				.catch((error: unknown) => {
					const err = error as Record<string, Record<string, unknown>>;
					handleErrorMessages(
						{
							errors: err?.message || err?.errors,
						},
						(msg: string) => toast(msg, { type: 'error' }),
					);
				});
		}

		timeOutRef.current = setTimeout(() => {
			fetchQuotesCount();
		}, 60000);
	}, []);

	const isInvoicedRedirectWithoutConfirmation =
		userDetails?.propertyManagerSettingsObj?.[
			'prefer-direct-invoice-provider-navigation'
		];

	useEffect(() => {
		fetchQuotesCount();

		return () => {
			if (timeOutRef.current) {
				clearTimeout(timeOutRef.current);
			}
		};
	}, [fetchQuotesCount]);

	return (
		<div className="global-sidebar offcanvas-collapse" id="sidebarNav">
			<aside className="sidebar-wrapper sidebar-collapse">
				<div className="sidebar-top-section">
					<div className="sidebar-logo-outer d-none d-lg-block">
						<Link href="/">
							<LogoIcon />
						</Link>
						<h5>Customer Dashboard</h5>
					</div>
					<div className="sidebar-top-section-mobile">
						{/* <Link to="#" className="mobile-notification unread" onClick={hideMenu}>
              <NotificationsIcon />
            </Link> */}
						<Link
							href="/my-profile"
							className="mobile-settings"
							prefetch
							onClick={hideMenu}
						>
							<SettingsIcon />
						</Link>
						<NavbarToggler
							onClick={hideMenu}
							className="mobile-navbar-toggler"
						/>
					</div>
					<div className="sidebar-asset-management">
						{userDetails?.partner?.partner_logo_url ? (
							<img
								src={userDetails.partner.partner_logo_url}
								alt=""
								width="27px"
							/>
						) : (
							userDetails?.partner?.label?.charAt(0).toUpperCase()
						)}
						<Link
							href={
								userDetails?.property_manager_type !== 'employee'
									? '/company-profile'
									: '#'
							}
							prefetch
							onClick={hideMenu}
						>
							<h5>
								{userDetails?.partner?.label}
								<br />
								Group
							</h5>
						</Link>
					</div>
					<ul className="admin-sidebar text-left">
						<li className="sidebar__list">
							<Link
								href="/dashboard"
								className={`sidebar__list--text${pathname === '/dashboard' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<DashboardIcon />
								</i>
								Dashboard
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/open-jobs"
								className={`sidebar__list--text${pathname === '/open-jobs' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<OpenJobsIcon />
								</i>
								Jobs In Progress
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/jobs"
								className={`sidebar__list--text${pathname === '/jobs' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<ClosedJobsIcon />
								</i>
								All Jobs
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/open-make-readies"
								className={`sidebar__list--text${pathname === '/open-make-readies' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<OpenMakeReadiesIcon />
								</i>
								Open Make-Readies
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/closed-make-readies"
								className={`sidebar__list--text${pathname === '/closed-make-readies' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<ClosedMakeReadiesIcon />
								</i>
								Closed Make-Readies
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/quotes"
								className={`sidebar__list--text${pathname === '/quotes' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<QuotesIcon />
									{quoteCount ? (
										<span className="item-count">{quoteCount}</span>
									) : (
										''
									)}
								</i>
								Quotes
							</Link>
						</li>
						<li className="sidebar__list">
							<Link
								href="/documents"
								className={`sidebar__list--text${pathname === '/documents' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<DocumentsIcon />
								</i>
								Documents
							</Link>
						</li>
						<li className="sidebar__list">
							<button
								type="button"
								className="sidebar__list--text"
								onClick={() => {
									hideMenu();

									if (isInvoicedRedirectWithoutConfirmation) {
										// User has preference set, redirect directly
										const invoicedUrl = userDetails?.invoice_provider_url;
										if (invoicedUrl) {
											openInANewTab(invoicedUrl);
										}
									} else {
										// Show modal instead of navigating
										dispatch(
											updateVisibility({
												modalType: 'invoicedRedirectModal',
												isVisible: true,
												extraProps: {
													invoicedUrl: userDetails?.invoice_provider_url,
												},
											}),
										);
									}
								}}
							>
								<i>
									<InvoicesIcon />
								</i>
								Invoices
							</button>
						</li>
						{userDetails?.flat_price_catalog?.link ? (
							<li className="sidebar__list">
								<a
									href={userDetails.flat_price_catalog.link}
									target="_blank"
									rel="noreferrer"
									className="sidebar__list--text"
									onClick={hideMenu}
								>
									<i className="catalog-icon">
										<FlatPriceCatalogSidebarIcon />
									</i>
									Flat Price Catalog
									<span className="custom-tag new">New</span>
								</a>
							</li>
						) : (
							''
						)}
						<li className="sidebar__list">
							<Link
								href="/help"
								className={`sidebar__list--text${pathname === '/help' ? ' active' : ''}`}
								prefetch
								onClick={hideMenu}
							>
								<i>
									<HelpIcon />
								</i>
								Help
							</Link>
						</li>
					</ul>
				</div>
				<div className="sidebar-bottom-section d-none d-lg-block">
					<LogGreyIcon />
					<div className="copoyright">
						Copyright <span>{new Date().getFullYear()}</span> &copy; All rights
						reserved.
					</div>
					<div className="cla">
						By using Lula you agree to our{' '}
						<Link href="/customer-service-agreement" prefetch>
							CSA
						</Link>
					</div>
				</div>
			</aside>
		</div>
	);
};

export default Sidebar;
